import { useMemo, useContext } from 'react'
import * as React from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BarCodeComponent from 'components/Pages/ExperienceVoucher/BarCode'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ExperiencesQRCode from 'components/Pages/ExperienceVoucher/ExperiencesQRCode'
import { INTL_SHORT_DATE_TIME_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

const LUX_QR_LOGO = <LuxuryEscapesSmallLogo />

const TitleText = styled.span`
  font-style: ${props => props.theme.font.secondaryAlternate.fontStyle};
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  font-weight: 450;
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
  gap: ${rem(16)};
`

const HeaderTitle = styled.span`
  font-size: ${rem(24)};
  line-height: 1.3;
  font-weight: 450;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const HeaderBanner = styled.div`
  display: flex;
  justify-content: space-between;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${props => props.theme.palette.neutral.surface.lightBackground} !important;
  }
`

const HeaderImageContainer = styled.div`
  width: 45%;
  max-width: ${rem(277)};
  aspect-ratio: 277 / 152;
  overflow: hidden;
  border-start-start-radius: ${rem(100)};
  border-end-start-radius: ${rem(100)};
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const VoucherDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  margin-top: ${rem(16)};
`

const ContentSection = styled.div`
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-start-start-radius: ${rem(4)};
  border-start-end-radius: ${rem(4)};
  border-end-end-radius: 0;
  border-end-start-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RedeemSection = styled.div`
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-start-start-radius: 0;
  border-start-end-radius: 0;
  border-end-end-radius: ${rem(4)};
  border-end-start-radius: ${rem(4)};
  border-top: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(32)};
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(16)} ${rem(40)};
`

const InfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const QRCodeWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  margin-top: ${rem(6)};
`

const PinWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(4)};
`

const VendorInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
  svg {
    margin-top: ${rem(2)};
  }
`

const VoucherCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${rem(32)} ${rem(24)};
  overflow-y: auto;
  @media print {
    padding: ${rem(24)} ${rem(20)};
  }
`

const VoucherCardContainer = styled(Pane)`
  width: 100%;
  max-width: ${rem(600)};
  aspect-ratio: 2 / 3;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 ${rem(4)} ${rem(16)} rgba(0, 0, 0, 0.1);
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  @media print {
    max-width: 100%;
    aspect-ratio: auto;
    height: 100vh;
    box-shadow: none;
    border: none;
  }
`

const ImportantNotice = styled.div`
  padding: ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${props => props.theme.palette.neutral.surface.lightBackground} !important;
  }
`

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(20)};
  margin-top: ${rem(24)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${props => props.theme.palette.neutral.surface.lightBackground} !important;
  }
`

const PhoneButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(16)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${rem(4)};
  text-decoration: none;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  font-weight: bold;
  @media print {
    &::after {
      content: '' !important;
    }
  }
`

interface VoucherProps {
  address: string
  voucher: App.ExperienceVoucher
  experienceOffer?: App.ExperienceOffer
}

export function PrintLoungeVoucher(props: VoucherProps) {
  const intl = useIntl()
  const { voucher, address, experienceOffer } = props
  const supportContact = useContext(ContactContext)

  const barcodeInfo = useMemo(() => {
    const type = voucher.offer.barcodeType
    const isCodeWithPin = type === 'CODE128 + pin'
    const parts = isCodeWithPin ? voucher.voucherCode.split('-') : [voucher.voucherCode]
    return {
      type: isCodeWithPin ? 'CODE128' : type,
      number: parts[0],
      pin: parts[1] ?? undefined,
    }
  }, [voucher])

  const qrCodeUrl = useMemo(() => {
    if (!voucher.qrCodeUrl) {
      const codeRequirements = voucher.offer.voucherCodeRequirements
      const showOnlyVoucherValue = voucher.offer.showOnlyVoucherValue
      const useExistingCodes = 'Use Existing Codes - Vendor provides us with codes to upload'

      if (codeRequirements === useExistingCodes || showOnlyVoucherValue) {
        return barcodeInfo.number
      }
      return `${config.LED_HOST}/login/r/${barcodeInfo.number}`
    }
    return voucher.qrCodeUrl
  }, [voucher, barcodeInfo])

  const guestsCountInfo = useMemo(() => {
    const tickets = voucher.ticketsInfo
    if (!Array.isArray(tickets) || tickets.length === 0) return ''
    return tickets
      .map(ticket => intl.formatMessage(
        {
          id: 'lbd9E5QZrI7uX7OoaZqg',
          defaultMessage: '{count} {ticketType}',
          description: 'Ticket quantity by type on lounge voucher guest info',
        },
        { count: ticket.quantity, ticketType: ticket.productId },
      ))
      .join(', ')
  }, [voucher.ticketsInfo, intl])

  const customerName = `${voucher.customer?.name} ${voucher.customer?.surname}`
  const bookingDate = intlDateFormatter(moment.utc(voucher.bookingStartDate).toDate(), INTL_SHORT_DATE_TIME_FORMAT, intl.locale, 'UTC')

  const isLuxBrand = useMemo(() => {
    return config.brandConfig?.subscriptionParentBrand === 'luxuryescapes'
  }, [])

  const addressLabel = intl.formatMessage({
    id: '6js6ISlsGTasE8VOV2-p',
    defaultMessage: 'Address',
    description: 'Form field label for vendor address on experience voucher',
  })

  const pinLabel = intl.formatMessage({
    id: 'rROgCdMpXEq4A7wioCJO',
    defaultMessage: 'PIN:',
    description: 'Label for voucher PIN code on lounge voucher top details',
  })

  const referenceCodeLabel = intl.formatMessage({
    id: 'j_zn1GiWtfGBV5kjbSeV',
    defaultMessage: 'REFERENCE CODE',
    description: 'Label for reference code on lounge voucher top details',
  })

  const leadGuestLabel = intl.formatMessage({
    id: '-OKNhmrk6zsVUnXBZmBX',
    defaultMessage: 'LEAD GUEST',
    description: 'Label for lead guest name on lounge voucher top details',
  })

  const bookingDateLabel = intl.formatMessage({
    id: '37CojMohQivSJfvKRIPM',
    defaultMessage: 'BOOKING DATE',
    description: 'Label for booking date on lounge voucher top details',
  })

  const whatToRememberLabel = intl.formatMessage({
    id: '1Nvk8-SzCMUGF206lVmD',
    defaultMessage: 'What to Remember',
    description: 'Section title for experience voucher things to remember',
  })

  return (
    <VoucherCardContainer border="light-foreground" borderRadius="M">
      <VoucherCardContent>
        <HeaderBanner>
          <HeaderContent>
            <LogoIcon
              mobileVariant="icon+text"
              tabletVariant="icon+text"
              desktopVariant="icon+text"
              height={24}
              width={125}
            />
            <HeaderTitle>
              <FormattedMessage
                id="-GTeLDsy8ExhEmTKhmwl"
                defaultMessage="Your <italic>experience</italic>{br}voucher"
                description="Header title on the experience voucher page for the experience"
                values={{
                  italic: (chunks: React.ReactNode) => <TitleText>{chunks}</TitleText>,
                  br: <br />,
                }}
              />
            </HeaderTitle>
          </HeaderContent>
          {experienceOffer?.images?.[0] && (
            <HeaderImageContainer>
              <Image
                image={experienceOffer.images[0]}
                width={277}
                height={152}
                fit="center"
              />
            </HeaderImageContainer>
          )}
        </HeaderBanner>
        <VoucherDetailsSection>
          <div>
            <ContentSection>
              <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="small" format="uppercase">
                    {voucher.location.name}
                  </BodyText>
                  <BodyText variant="medium" weight="bold">
                    {voucher.offer.vendorName}
                  </BodyText>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {voucher.offerPackage.name}
                    </BodyText>
                    {voucher.offerPackage.description && (
                      <BodyText variant="small">
                        {voucher.offerPackage.description}
                      </BodyText>
                    )}
                  </Group>
                </Group>
                <QRCodeWrapper>
                  {barcodeInfo.type === 'QR_CODE' && (
                    <ExperiencesQRCode
                      value={qrCodeUrl}
                      size={110}
                      logo={voucher.qrCodeLogoUrl ? voucher.qrCodeLogoUrl : isLuxBrand ? LUX_QR_LOGO : undefined}
                    />
                  )}
                  {!!barcodeInfo.type && ['CODE39', 'CODE128'].includes(barcodeInfo.type) && (
                    <BarCodeComponent
                      width={1}
                      height={50}
                      format={barcodeInfo.type}
                      displayValue={false}
                      value={barcodeInfo.number}
                    />
                  )}
                  {barcodeInfo.pin && (
                    <PinWrapper>
                      <BodyText variant="small">{pinLabel} </BodyText>
                      <BodyText weight="bold" variant="medium">
                        {barcodeInfo.pin}
                      </BodyText>
                    </PinWrapper>
                  )}
                </QRCodeWrapper>
              </Group>
              <InfoGrid>
                <InfoCell>
                  <BodyText weight="bold" variant="small">{bookingDateLabel}</BodyText>
                  <BodyText variant="small" weight="bold">{bookingDate}</BodyText>
                </InfoCell>
                <InfoCell>
                  <BodyText weight="bold" variant="small">{leadGuestLabel}</BodyText>
                  <BodyText variant="small" weight="bold">{customerName}</BodyText>
                </InfoCell>
                <InfoCell>
                  <BodyText variant="small" weight="bold">{referenceCodeLabel}</BodyText>
                  <BodyText variant="small" weight="bold" wrap="letter-wrap">
                    {voucher.voucherCode}
                  </BodyText>
                </InfoCell>
                {guestsCountInfo && (
                  <InfoCell>
                    <BodyText variant="small" weight="bold">
                      {intl.formatMessage({
                        id: 'E7SeBDxEcRDfr2AIwJ-n',
                        defaultMessage: 'Guests',
                        description: 'Label for guests count on lounge voucher for promotions',
                      })}
                    </BodyText>
                    <BodyText variant="small" weight="bold">
                      {guestsCountInfo}
                    </BodyText>
                  </InfoCell>
                )}
              </InfoGrid>
            </ContentSection>
            <RedeemSection>
              {voucher.offer.whatToRemember && (
                <Group direction="vertical" gap={8}>
                  <BodyText variant="medium" weight="bold">
                    {whatToRememberLabel}
                  </BodyText>
                  <BodyText variant="small" colour="light-foreground-alt">
                    {voucher.offer.whatToRemember}
                  </BodyText>
                </Group>
              )}
              <ImportantNotice>
                <BodyText variant="small">
                  <FormattedMessage
                    id="C06mDbEJbjvJsES-JjIX"
                    defaultMessage="<b>IMPORTANT:</b> The lounge access duration is <b>limited to 3 hours</b>."
                    description="Important notice about lounge access duration limit on lounge voucher"
                    values={{ b: (chunks) => <b>{chunks}</b> }}
                  />
                </BodyText>
              </ImportantNotice>
              <Group direction="vertical" gap={12}>
                <BodyText variant="medium" weight="bold">
                  {voucher.offer.vendorName}
                </BodyText>
                <VendorInfoRow>
                  <LineMapMarkerIcon />
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="small" weight="bold">{addressLabel}</BodyText>
                    <BodyText variant="small">{address}</BodyText>
                  </Group>
                </VendorInfoRow>
              </Group>
            </RedeemSection>
          </div>
        </VoucherDetailsSection>
        <FooterSection>
          <BodyText variant="medium" weight="bold">
            <FormattedMessage
              id="AE3v4Zm78Um1yMF0w8oh"
              defaultMessage="Need help? Call our support team"
              description="Heading for help pane component in the customer support section"
            />
          </BodyText>
          <BodyText variant="small" colour="light-foreground-alt">
            <FormattedMessage
              id="ZcQBSJtId5EsH-9oVrPX"
              defaultMessage="Our customer service team are available anytime, 24 hours, 7 days a week"
              description="Default description text for help pane component in the experiences section"
            />
          </BodyText>
          <PhoneButton href={`tel:${supportContact.local.number}`}>
            <LinePhoneIcon />
            {supportContact.local.humanReadable}
          </PhoneButton>
        </FooterSection>
      </VoucherCardContent>
    </VoucherCardContainer>
  )
}
